import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { formatTimeSlotLabel, snapToThirtyMinuteSlot } from 'src/helpers/scheduler-time.helpers';
import { SchedulerTimePicker } from 'src/pages/operations/components/scheduler-time-picker';
import { useSchedulingSettings } from '../hooks/use-scheduling-settings';

interface BookingWindowSettingsProps {
  disabled?: boolean;
}

function apiTimeToPickerValue(value: string | undefined): string {
  if (!value) {
    return '10:00';
  }
  return snapToThirtyMinuteSlot(value.slice(0, 5));
}

export function BookingWindowSettings({ disabled = false }: BookingWindowSettingsProps) {
  const { settings, isLoading, isError, isSaving, saveBookingWindow } = useSchedulingSettings();
  const [startTime, setStartTime] = useState('10:00');
  const [endTime, setEndTime] = useState('16:00');
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setStartTime(apiTimeToPickerValue(settings.booking_start_time));
      setEndTime(apiTimeToPickerValue(settings.booking_end_time));
    }
  }, [settings]);

  if (isLoading) {
    return (
      <div className="mb-6 flex items-center gap-2 text-[#5E6278]">
        <Loader2
          className="size-4 animate-spin"
          aria-hidden
        />
        <span className={portalDs.caption}>
          Loading booking window…
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <p className={cn(portalDs.caption, 'mb-6 text-[#DC2626]')}>
        Could not load the booking window settings.
      </p>
    );
  }

  const handleSave = async() => {
    setInlineError(null);

    if (startTime >= endTime) {
      setInlineError('Finish time must be after start time.');
      return;
    }

    const result = await saveBookingWindow(startTime, endTime);
    if (!result.ok) {
      setInlineError(result.message);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-[#EDEEF2] bg-white p-4 shadow-[0_1px_2px_rgba(13,15,26,0.05)]">
      <p className="text-[13px] font-semibold text-[#0D0F1A]">
        Booking window
      </p>
      <p className={cn(portalDs.caption, 'mt-1')}>
        Client-facing availability is offered between these times (Central Time), Monday to Friday.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor="booking-window-start"
            className="mb-2 block text-[13px] font-medium text-[#0D0F1A]"
          >
            Start time
          </label>
          <SchedulerTimePicker
            id="booking-window-start"
            value={startTime}
            onChange={setStartTime}
            disabled={disabled || isSaving}
          />
        </div>
        <div>
          <label
            htmlFor="booking-window-end"
            className="mb-2 block text-[13px] font-medium text-[#0D0F1A]"
          >
            Finish time
          </label>
          <SchedulerTimePicker
            id="booking-window-end"
            value={endTime}
            onChange={setEndTime}
            disabled={disabled || isSaving}
          />
        </div>
      </div>

      <p className={cn(portalDs.caption, 'mt-3')}>
        Current window:
        {' '}
        {formatTimeSlotLabel(startTime)}
        {' '}
        –
        {' '}
        {formatTimeSlotLabel(endTime)}
        {' '}
        CT
      </p>

      <div className="mt-4">
        <button
          type="button"
          disabled={disabled || isSaving}
          onClick={() => void handleSave()}
          className="inline-flex h-9 items-center justify-center rounded-lg bg-[#0D0F1A] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2A2D3E] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save booking window'}
        </button>
      </div>

      {inlineError ? (
        <p className={cn(portalDs.caption, 'mt-2 text-[#DC2626]')}>
          {inlineError}
        </p>
      ) : null}
    </div>
  );
}
